import React, { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorBoundary, FallbackProps } from '../../../components/error-boundary';
import { MotionModal } from '../../../components/motion-modal/motion-modal';
import { ImageAspectRatioType } from '../../../interfaces';
import { SeriesRecording, SingleRecording } from '../../../queries/ats/recordings/types/recording';
import { Asset, EpisodeAsset } from '../../../queries/ats/titles/types/asset';
import { ProgramSource } from '../../../queries/ats/titles/types/sources';
import { useDetailsImage } from '../use-details-image';
import { appendSequenceNumbers } from '../utils';
import { DeleteModeToggle } from './delete-mode-toggle';
import css from './record-modal.module.scss';
import { RecordProgramButton, SingleRecordingButton } from './record-program-button';
import { RecordQueueProvider, useRecordQueue } from './record-queue';
import { CancelSeriesRecordingButton, RecordSeriesButtons } from './record-series-button';

export interface RecordModalProps {
  asset: Asset;
  source: ProgramSource;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

export function RecordModal({ asset, source, isOpen, setIsOpen }: RecordModalProps) {
  const image = useDetailsImage({
    asset: asset,
    shape: ImageAspectRatioType.WIDE,
    width: 300,
  });

  if (asset.type === 'episode') {
    return <RecordEpisodeModal asset={asset as EpisodeAsset} source={source} isOpen={isOpen} setIsOpen={setIsOpen} />;
  }

  const title = asset.titleName;

  const recording = source.recordings.find((rec): rec is SingleRecording => rec.type === 'single');

  return (
    <MotionModal classNames={{ content: css.modal }} isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
      <MotionModal.Header image={image} title={appendSequenceNumbers(title, asset)} />
      <MotionModal.CloseButton onClick={() => setIsOpen(false)} />
      <div className={css.container}>
        <RecordQueueProvider>
          <RecordErrorBoundary>
            {recording === undefined ? (
              <RecordProgramButton asset={asset} source={source} />
            ) : (
              <SingleRecordingButton recording={recording} />
            )}

            <DeleteModeToggle recordings={source.recordings} />
          </RecordErrorBoundary>
        </RecordQueueProvider>
      </div>
    </MotionModal>
  );
}

interface RecordEpisodeModalProps extends RecordModalProps {
  asset: EpisodeAsset;
}

function RecordEpisodeModal({ asset, source, isOpen, setIsOpen }: RecordEpisodeModalProps) {
  const image = useDetailsImage({
    asset: asset,
    shape: ImageAspectRatioType.WIDE,
    width: 300,
  });

  const title = asset.series.titleName;

  const single = source.recordings.find((rec): rec is SingleRecording => rec.type === 'single');
  const series = source.recordings.find((rec): rec is SeriesRecording => rec.type === 'series');

  return (
    <MotionModal classNames={{ content: css.modal }} isOpen={isOpen} onRequestClose={() => setIsOpen(false)}>
      <MotionModal.Header image={image} title={appendSequenceNumbers(title, asset)} />
      <MotionModal.CloseButton onClick={() => setIsOpen(false)} />
      <div className={css.container}>
        <RecordQueueProvider>
          <RecordErrorBoundary>
            {single !== undefined ? (
              <SingleRecordingButton recording={single} />
            ) : (
              <RecordProgramButton asset={asset} source={source} />
            )}

            {series !== undefined ? (
              <SeriesRecordingButtons recording={series} asset={asset} source={source} />
            ) : (
              <RecordSeriesButtons asset={asset} source={source} />
            )}

            <DeleteModeToggle recordings={source.recordings} />
          </RecordErrorBoundary>
        </RecordQueueProvider>
      </div>
    </MotionModal>
  );
}

interface SeriesRecordingButtonsProps {
  recording: SeriesRecording;
  asset: EpisodeAsset;
  source: ProgramSource;
}

function SeriesRecordingButtons({ recording, asset, source }: SeriesRecordingButtonsProps) {
  if (recording.status === 'scheduled') {
    return <CancelSeriesRecordingButton recording={recording} />;
  }

  return <RecordSeriesButtons asset={asset} source={source} />;
}

interface RecordErrorBoundaryProps {
  children: React.ReactNode;
}

function RecordErrorBoundary({ children }: RecordErrorBoundaryProps) {
  const queue = useRecordQueue();

  return (
    <ErrorBoundary
      fallback={(props) => <RecordErrorBoundaryFallback {...props} />}
      onError={() => {
        queue.clear();
      }}
    >
      {children}
    </ErrorBoundary>
  );
}

function RecordErrorBoundaryFallback({ error, reset }: FallbackProps) {
  const { t } = useTranslation();

  return (
    <Fragment>
      <p>{error.message}</p>
      <MotionModal.Button onClick={() => reset()}>{t('try_again')}</MotionModal.Button>
    </Fragment>
  );
}
